import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowUpRight, ExternalLink } from 'lucide-react'
import { topDestinosExecutiva } from '../data'
import type { Destino } from '../data'

type FiltroContinente = 'Todos' | Destino['continente']
type FiltroPrograma = 'Todos' | Destino['programa']

const continentes: FiltroContinente[] = [
  'Todos',
  'Europa',
  'América do Norte',
  'América Latina',
  'Ásia',
  'Oriente Médio',
  'África',
]

const programas: FiltroPrograma[] = ['Todos', 'Smiles', 'LATAM Pass', 'Azul']

export function TopDestinos() {
  const [cont, setCont] = useState<FiltroContinente>('Todos')
  const [prog, setProg] = useState<FiltroPrograma>('Todos')

  const filtered = useMemo(() => {
    let list = [...topDestinosExecutiva]
    if (cont !== 'Todos') list = list.filter((d) => d.continente === cont)
    if (prog !== 'Todos') list = list.filter((d) => d.programa === prog)
    return list.sort((a, b) => a.milhasMin - b.milhasMin)
  }, [cont, prog])

  return (
    <section id="destinos" className="bg-cream py-16 md:py-24">
      <div className="container-editorial">
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-12">
          <div>
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-taupe" />
              <span className="text-xs tracking-[0.35em] uppercase text-taupe-dark font-medium">
                Top destinos · executiva
              </span>
            </div>
            <h2 className="font-display font-light text-3xl md:text-5xl leading-[1.05] tracking-tighter text-ink">
              Voe em <em className="text-taupe-dark">executiva</em> pagando em milhas.
            </h2>
            <p className="mt-3 text-base text-ink/60 max-w-xl">
              Passagens reais observadas e ordenadas pela menor quantidade
              de milhas. Cada link abre direto no motor do programa com
              busca preenchida.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {programas.map((p) => (
              <button
                key={p}
                onClick={() => setProg(p)}
                aria-pressed={prog === p}
                className={`text-xs tracking-wide px-4 py-2 rounded-full transition-all ${
                  prog === p
                    ? 'bg-ink text-bone'
                    : 'bg-bone border border-taupe/30 text-ink/70 hover:border-ink'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </header>

        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 -mx-2 px-2">
          {continentes.map((c) => (
            <button
              key={c}
              onClick={() => setCont(c)}
              aria-pressed={cont === c}
              className={`shrink-0 text-xs tracking-wide px-4 py-2 rounded-full transition-all ${
                cont === c
                  ? 'bg-ink text-bone'
                  : 'bg-bone border border-taupe/30 text-ink/70 hover:border-ink'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {filtered.length === 0 && (
            <div className="col-span-full py-16 text-center text-sm text-taupe">
              Nenhum destino com esses filtros no momento.
            </div>
          )}
          {filtered.map((d, i) => (
            <motion.article
              key={d.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
              className="group bg-bone rounded-2xl border border-taupe/20 overflow-hidden hover:border-ink/40 transition-all flex flex-col"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-taupe/20">
                <motion.img
                  src={d.imagem}
                  alt={`${d.cidade}, ${d.pais}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                  whileHover={{ scale: 1.04 }}
                  transition={{ duration: 1 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-2">
                  <span className="inline-flex items-center text-[10px] tracking-[0.3em] uppercase bg-bone/95 text-ink px-2.5 py-1 rounded-full">
                    {d.continente}
                  </span>
                  <span className="inline-flex items-center text-[10px] tracking-wider uppercase bg-ink/80 text-bone px-2.5 py-1 rounded-full">
                    {d.programa}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 text-bone">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-bone/85">
                    {d.origem} → {d.codigoIATA}
                  </div>
                  <div className="font-display font-light text-2xl md:text-3xl leading-tight mt-0.5">
                    {d.cidade}
                  </div>
                  <div className="text-xs text-bone/80">{d.pais}</div>
                </div>
              </div>

              <div className="p-5 md:p-6 flex-1 flex flex-col">
                <div className="grid grid-cols-2 gap-4 pb-4 border-b border-taupe/15">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-taupe mb-1">
                      A partir de
                    </div>
                    <div className="font-sans font-semibold text-2xl text-ink tabular-nums leading-none">
                      {d.milhasMin.toLocaleString('pt-BR')}
                    </div>
                    <div className="text-[11px] text-taupe-dark mt-1">
                      milhas · {d.trecho}
                    </div>
                  </div>
                  <div className="text-right">
                    {d.taxasReais ? (
                      <>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-taupe mb-1">
                          Taxas
                        </div>
                        <div className="text-lg text-ink/85 tabular-nums font-medium">
                          R$ {d.taxasReais.toLocaleString('pt-BR')}
                        </div>
                        <div className="text-[11px] text-taupe-dark mt-1">embarque</div>
                      </>
                    ) : d.precoReaisTotal ? (
                      <>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-taupe mb-1">
                          Equiv. dinheiro
                        </div>
                        <div className="text-lg text-ink/85 tabular-nums font-medium">
                          R$ {d.precoReaisTotal.toLocaleString('pt-BR')}
                        </div>
                        <div className="text-[11px] text-taupe-dark mt-1">total</div>
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-xs text-taupe-dark mt-3">
                  <span className="font-medium text-ink/75">{d.companhia}</span>
                </div>

                {d.observacoes && (
                  <p className="mt-3 text-xs text-ink/60 leading-relaxed">
                    {d.observacoes}
                  </p>
                )}

                <div className="mt-auto pt-4 flex flex-col gap-2">
                  <a
                    href={d.urlResgate}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-ink text-bone px-4 py-2.5 rounded-full hover:bg-taupe-dark transition-colors text-xs tracking-wide group/btn"
                  >
                    Abrir no {d.programa}
                    <ArrowUpRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                  </a>
                  <a
                    href={d.fonteMateria}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 text-[10px] text-taupe hover:text-ink transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Fonte: {d.fonteNome} ({d.dataMateria})
                  </a>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-8 pt-6 border-t border-taupe/20 flex flex-col md:flex-row md:items-center gap-3 justify-between">
          <p className="text-xs text-taupe leading-relaxed max-w-2xl">
            ⓘ {filtered.length} destinos filtrados · dados coletados de matérias
            recentes (jan–mar/2026) do Passageiro de Primeira e Melhores Destinos ·
            disponibilidade e preço podem variar ao clicar no link do motor.
          </p>
          <a href="#entrar" className="text-xs text-ink/70 hover:text-ink inline-flex items-center gap-1.5 group shrink-0">
            Alerta quando milhas caírem
            <span className="group-hover:translate-x-0.5 transition-transform">→</span>
          </a>
        </div>
      </div>
    </section>
  )
}
